import { DropdownActionItem, DropdownActions } from '../../../../widgets/dropdown';
import { QuestionDialog, useDialogManager } from '../../../../widgets/dialog';
import { Icon } from '../../../../widgets/icon';
import React, { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from 'react-relay';
import ExpandIcon from 'src/shared/material_icons/navigation/expand_more.svg';
import CollapseIcon from 'src/shared/material_icons/navigation/expand_less.svg';
import RedoIcon from 'src/shared/material_icons/content/redo.svg';
import SaveIcon from 'src/shared/material_icons/content/save.svg';
import SettingsIcon from 'src/shared/material_icons/action/settings.svg';
import UndoIcon from 'src/shared/material_icons/content/undo.svg';
import styled from 'styled-components';
import { ProductStatus } from '../../../../../shared/enums';
import { backofficeUrl, T } from '../../../../../shared/helpers';
import LogoZakeke from '../../../../../shared/svg/logoZakeke';
import { showErrorNotification, showInfoNotification, showSuccessNotification } from '../../../../widgets/notificationToaster';
import { puSwitchProductSetStatusMutation } from '../../../products/__generated__/puSwitchProductSetStatusMutation.graphql';
import { productStatusMutation } from '../../../products/productStatusMutation';
import { setProductStatusAction, useProductConfigurationChangeStep, useProductConfigurationState } from '../../state';
import DesktopWideIcon from './icons/desktop-wide.svg';
import DesktopIcon from './icons/desktop.svg';
import MobileIcon from './icons/mobile.svg';

type ComposerMode = 'scene' | 'composer' | 'test';

const HeaderContainer = styled.header`
    display: grid;
    height: 52px;
    flex: 0 0 52px;
    grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
    align-items: center;
    gap: 14px;
    padding: 0 18px;
    color: var(--composer-fg, #1e2a38);
    background: linear-gradient(rgb(0 0 0 / 6%), rgb(0 0 0 / 6%)), #fff;
    font-family:
        Inter,
        -apple-system,
        BlinkMacSystemFont,
        'Segoe UI',
        system-ui,
        sans-serif;
    z-index: 40;
`;

const HeaderLeft = styled.div`
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 8px;
`;

const HeaderRight = styled.div`
    display: flex;
    min-width: 0;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
`;

const MarkLink = styled.a`
    display: block;
    width: 32px;
    height: 32px;
    flex: 0 0 32px;
    overflow: hidden;
    border-radius: 8px;
    background: #fff;
    box-shadow:
        rgba(30, 42, 56, 0.05) 0 1px 2px,
        rgba(30, 42, 56, 0.06) 0 0 0 1px;

    > svg {
        width: 130px;
        height: 32px;
        max-width: none;
    }
`;

const Identity = styled.div`
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 8px;
`;

const ProductName = styled.div`
    max-width: 220px;
    overflow: hidden;
    font-size: 12.5px;
    font-weight: 600;
    line-height: 18px;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const ProductStatusLabel = styled.span<{ $published: boolean }>`
    display: inline-flex;
    height: 24px;
    flex: 0 0 auto;
    align-items: center;
    gap: 6px;
    padding: 0 8px;
    color: var(--composer-muted, #5c6b7a);
    background: var(--composer-sunken, #f4f7fa);
    border-radius: 999px;
    box-shadow: inset 0 0 0 1px var(--composer-line, #dce3eb);
    font-size: 10.5px;

    &::before {
        width: 6px;
        height: 6px;
        background: ${(props) => (props.$published ? '#177c4c' : 'var(--composer-faint, #677078)')};
        border-radius: 50%;
        content: '';
    }
`;

const Divider = styled.span`
    width: 1px;
    height: 20px;
    flex: 0 0 1px;
    margin: 0 6px 0 4px;
    background: var(--composer-line, #dce3eb);
`;

const IconButton = styled.button<{ $active?: boolean }>`
    display: inline-flex;
    width: 30px;
    height: 30px;
    flex: 0 0 30px;
    align-items: center;
    justify-content: center;
    padding: 0;
    color: ${(props) => (props.$active ? 'var(--composer-accent-text, #a8480b)' : 'var(--composer-muted, #5c6b7a)')};
    background: ${(props) => (props.$active ? 'rgb(0 0 0 / 6%)' : 'transparent')};
    border: 0;
    border-radius: 8px;
    cursor: pointer;

    &:hover:not(:disabled),
    &:focus-visible {
        color: var(--composer-fg, #1e2a38);
        background: var(--composer-hover, #e9eff5);
        outline: none;
    }

    &:disabled {
        cursor: default;
        opacity: 0.34;
    }

    ${Icon},
    svg {
        width: 17px;
        height: 17px;
        fill: currentColor;
    }
`;

const History = styled.div`
    display: flex;
    align-items: center;
    gap: 2px;
`;

const Modes = styled.div<{ $index: number }>`
    --modes-index: ${(props) => props.$index};
    position: relative;
    display: grid;
    grid-auto-columns: 1fr;
    grid-auto-flow: column;
    padding: 3px;
    background: var(--composer-sunken, #f4f7fa);
    border-radius: 999px;
    box-shadow: inset 0 0 0 1px var(--composer-line, #dce3eb);

    &::before {
        position: absolute;
        top: 3px;
        bottom: 3px;
        left: 3px;
        width: calc((100% - 6px) / 3);
        background: #fff;
        border-radius: 999px;
        box-shadow:
            rgba(30, 42, 56, 0.05) 0 1px 2px,
            rgba(30, 42, 56, 0.06) 0 0 0 1px;
        content: '';
        transform: translateX(calc(var(--modes-index) * 100%));
        transition: transform 240ms cubic-bezier(0.4, 0, 0.2, 1);
    }
`;

const ModeButton = styled.button<{ $current: boolean }>`
    position: relative;
    z-index: 1;
    padding: 5px 14px;
    color: ${(props) => (props.$current ? 'var(--composer-fg, #1e2a38)' : 'var(--composer-muted, #5c6b7a)')};
    background: transparent;
    border: 0;
    border-radius: 999px;
    cursor: pointer;
    font: inherit;
    font-size: 12.5px;
    font-weight: 500;
    white-space: nowrap;

    &:hover,
    &:focus-visible {
        color: var(--composer-fg, #1e2a38);
        outline: none;
    }
`;

const DeviceModes = styled.div`
    display: flex;
    align-items: center;
    gap: 2px;
    margin-left: 6px;
`;

const SaveState = styled.span`
    margin: 0 6px 0 2px;
    color: var(--composer-faint, #677078);
    font-size: 11.5px;
    white-space: nowrap;
`;

const CloseButton = styled.button`
    height: 30px;
    padding: 0 11px;
    color: var(--composer-muted, #5c6b7a);
    background: transparent;
    border: 0;
    border-radius: 8px;
    cursor: pointer;
    font: inherit;
    font-size: 12px;
    font-weight: 500;

    &:hover,
    &:focus-visible {
        color: var(--composer-fg, #1e2a38);
        background: var(--composer-hover, #e9eff5);
        outline: none;
    }
`;

const Split = styled.div`
    display: flex;
    align-items: stretch;
`;

const SaveButton = styled.button<{ $caret?: boolean }>`
    display: inline-flex;
    height: 30px;
    align-items: center;
    justify-content: center;
    gap: ${(props) => (props.$caret ? '0' : '6px')};
    padding: ${(props) => (props.$caret ? '0 7px' : '0 13px')};
    color: #fff;
    background: var(--composer-accent, #e8650a);
    border: 0;
    border-radius: ${(props) => (props.$caret ? '0 8px 8px 0' : '8px 0 0 8px')};
    box-shadow: ${(props) => (props.$caret ? 'inset 1px 0 0 var(--composer-accent-deep, #c25309)' : 'none')};
    cursor: pointer;
    font: inherit;
    font-size: 12px;
    font-weight: 600;

    &:hover:not(:disabled),
    &:focus-visible {
        background: var(--composer-accent-deep, #c25309);
        outline: none;
    }

    &:disabled {
        cursor: default;
        opacity: 0.55;
    }

    svg {
        width: 12px;
        height: 12px;
        fill: currentColor;
    }
`;

type HeaderToolbarProps = {
    activeMode?: ComposerMode;
    closeOverlay?: () => void;
    previewSize?: 'desktop-wide' | 'desktop' | 'mobile';
    changePreviewSize?: (size: 'desktop-wide' | 'desktop' | 'mobile') => void;
    isShoppingPreview?: boolean;
    onSettings?: () => void;
    onSave?: () => boolean | void | Promise<boolean | void>;
    isSaving?: boolean;
    hasUnsavedChanges?: boolean;
};

const HeaderToolbar: FC<HeaderToolbarProps> = ({
    activeMode = 'composer',
    previewSize,
    changePreviewSize,
    isShoppingPreview,
    closeOverlay,
    onSettings,
    onSave,
    isSaving,
    hasUnsavedChanges = false
}) => {
    const { state, dispatch, unsavedChanges } = useProductConfigurationState();
    const { showDialog, closeDialog } = useDialogManager();
    const [setStatusProduct, isUpdatingStatus] = useMutation<puSwitchProductSetStatusMutation>(productStatusMutation);
    const changeStep = useProductConfigurationChangeStep();
    const navigate = useNavigate();
    const modeIndex = activeMode === 'scene' ? 0 : activeMode === 'composer' ? 1 : 2;
    const isDirty = hasUnsavedChanges || unsavedChanges.isDirty;
    const isPublished = state.product.statusID === ProductStatus.Published;
    const isBusy = Boolean(isSaving || isUpdatingStatus);

    const goToMode = (mode: ComposerMode) => {
        if (mode === 'scene') changeStep('3D-product-configurator-scene-editor');
        if (mode === 'composer') changeStep('3D-product-configurator-composer');
        if (mode === 'test') changeStep('3D-product-configurator-shopping-preview');
    };

    const requestClose = () => {
        closeOverlay?.();
        if (!isDirty) {
            navigate(backofficeUrl('/products'));
            return;
        }
        showDialog(
            'close-dialog',
            <QuestionDialog
                message={
                    <React.Fragment>
                        <p>
                            {T._('You will lose all unsaved changes.', 'Admin')}
                            <br />
                            {T._('Do you wish to continue?', 'Admin')}
                        </p>
                    </React.Fragment>
                }
                buttonYesLabel={T._('Confirm', 'Admin')}
                buttonNoLabel={T._('Cancel', 'Admin')}
                onYesClick={() => {
                    closeDialog('close-dialog');
                    navigate(backofficeUrl('/products'));
                }}
                onNoClick={() => closeDialog('close-dialog')}
            />
        );
    };

    const saveChanges = async (notifyWhenClean = true) => {
        if (!isDirty) {
            if (notifyWhenClean) showInfoNotification({ message: T._('Nothing to save — everything is up to date.', 'Admin') });
            return true;
        }
        if (!onSave) {
            showErrorNotification({ message: T._('Saving is not available in this view.', 'Admin') });
            return false;
        }

        try {
            const saved = await onSave();
            return saved !== false;
        } catch {
            return false;
        }
    };

    const changePublicationStatus = async (nextStatus: ProductStatus) => {
        if (!state.product.id || isBusy) return;
        if (isDirty && !(await saveChanges(false))) return;

        const publishing = nextStatus === ProductStatus.Published;
        const changed = await new Promise<boolean>((resolve) => {
            setStatusProduct({
                variables: {
                    input: {
                        productID: state.product.id!,
                        status: publishing ? 'PUBLISHED' : 'UN_PUBLISHED'
                    }
                },
                onCompleted: (response, errors) => {
                    const messages = [
                        ...(errors?.map((error) => error.message) ?? []),
                        ...(response.setStatusProduct?.errors?.map((error) => error.message).filter(Boolean) ?? [])
                    ];
                    if (!response.setStatusProduct?.model || messages.length > 0) {
                        showErrorNotification({
                            message:
                                messages.join(' ') ||
                                (publishing ? T._('Failed to publish the product.', 'Admin') : T._('Failed to unpublish the product.', 'Admin'))
                        });
                        resolve(false);
                        return;
                    }

                    dispatch(setProductStatusAction(nextStatus));
                    resolve(true);
                },
                onError: () => {
                    showErrorNotification({
                        message: publishing ? T._('Failed to publish the product.', 'Admin') : T._('Failed to unpublish the product.', 'Admin')
                    });
                    resolve(false);
                }
            });
        });

        if (changed) {
            showSuccessNotification({
                message: publishing ? T._('Product published.', 'Admin') : T._('Product unpublished.', 'Admin')
            });
        }
    };

    return (
        <HeaderContainer>
            <HeaderLeft>
                <MarkLink href={backofficeUrl('/Dashboard')} aria-label={T._('Zakeke dashboard', 'Admin')}>
                    <LogoZakeke />
                </MarkLink>
                <Identity>
                    <ProductName title={state.product.name}>{state.product.name || T._('Product configurator', 'Admin')}</ProductName>
                    <ProductStatusLabel $published={isPublished}>{isPublished ? T._('Published', 'Admin') : T._('Not published', 'Admin')}</ProductStatusLabel>
                </Identity>
                <Divider />
                <History aria-label={T._('History', 'Admin')}>
                    <IconButton disabled aria-label={T._('Undo', 'Admin')}>
                        <Icon>
                            <UndoIcon />
                        </Icon>
                    </IconButton>
                    <IconButton disabled aria-label={T._('Redo', 'Admin')}>
                        <Icon>
                            <RedoIcon />
                        </Icon>
                    </IconButton>
                </History>
                {isShoppingPreview && (
                    <DeviceModes>
                        <IconButton $active={previewSize === 'desktop-wide'} onClick={() => changePreviewSize?.('desktop-wide')}>
                            <Icon>
                                <DesktopWideIcon />
                            </Icon>
                        </IconButton>
                        <IconButton $active={previewSize === 'desktop'} onClick={() => changePreviewSize?.('desktop')}>
                            <Icon>
                                <DesktopIcon />
                            </Icon>
                        </IconButton>
                        <IconButton $active={previewSize === 'mobile'} onClick={() => changePreviewSize?.('mobile')}>
                            <Icon>
                                <MobileIcon />
                            </Icon>
                        </IconButton>
                    </DeviceModes>
                )}
            </HeaderLeft>
            <Modes $index={modeIndex} aria-label={T._('Configurator mode', 'Admin')} role='tablist'>
                <ModeButton $current={activeMode === 'scene'} aria-selected={activeMode === 'scene'} onClick={() => goToMode('scene')} role='tab'>
                    {T._('3D Scene Editor', 'Admin')}
                </ModeButton>
                <ModeButton $current={activeMode === 'composer'} aria-selected={activeMode === 'composer'} onClick={() => goToMode('composer')} role='tab'>
                    {T._('Composer', 'Admin')}
                </ModeButton>
                <ModeButton $current={activeMode === 'test'} aria-selected={activeMode === 'test'} onClick={() => goToMode('test')} role='tab'>
                    {T._('Test Mode', 'Admin')}
                </ModeButton>
            </Modes>
            <HeaderRight>
                <IconButton aria-label={T._('Settings', 'Admin')} onClick={onSettings}>
                    <Icon>
                        <SettingsIcon />
                    </Icon>
                </IconButton>
                <SaveState aria-live='polite'>
                    {isSaving
                        ? T._('Saving…', 'Admin')
                        : isUpdatingStatus
                          ? T._('Updating publication…', 'Admin')
                          : isDirty
                            ? T._('Unsaved changes', 'Admin')
                            : T._('Saved', 'Admin')}
                </SaveState>
                <Split>
                    <SaveButton disabled={isBusy} onClick={() => void saveChanges()}>
                        <SaveIcon />
                        {isSaving ? T._('Saving…', 'Admin') : T._('Save', 'Admin')}
                    </SaveButton>
                    <DropdownActions
                        header={({ isOpen }) => (
                            <SaveButton $caret disabled={isBusy} aria-expanded={isOpen} aria-label={T._('More save options', 'Admin')}>
                                {isOpen ? <CollapseIcon /> : <ExpandIcon />}
                            </SaveButton>
                        )}
                        items={[
                            <DropdownActionItem key='save' caption={T._('Keep your changes', 'Admin')} disabled={isBusy} onClick={() => void saveChanges()}>
                                {T._('Save', 'Admin')}
                            </DropdownActionItem>,
                            <DropdownActionItem
                                key='publish'
                                caption={T._('Put this product in the storefront', 'Admin')}
                                disabled={isBusy || isPublished || !state.product.id}
                                onClick={() => void changePublicationStatus(ProductStatus.Published)}
                            >
                                {T._('Publish', 'Admin')}
                            </DropdownActionItem>,
                            <DropdownActionItem
                                key='unpublish'
                                caption={T._('Take it out of the storefront', 'Admin')}
                                disabled={isBusy || !isPublished || !state.product.id}
                                onClick={() => void changePublicationStatus(ProductStatus.UnPublished)}
                            >
                                {T._('Unpublish', 'Admin')}
                            </DropdownActionItem>
                        ]}
                        minWidth='220px'
                        orientation='RIGHT'
                    />
                </Split>
                <CloseButton onClick={requestClose}>{T._('Close', 'Admin')}</CloseButton>
            </HeaderRight>
        </HeaderContainer>
    );
};

export default HeaderToolbar;
